import { T } from '@tolgee/react';
import styled from 'styled-components';

import ChevronRight from '../../../assets/icons/ChevronRight';
import theme from '../../../assets/theme';

interface SchoolPlanTrialsCounterProps {
  trialsLeft: number;
  trialLimit: number;
  onClick?: () => void;
}

export function SchoolPlanTrialsCounter({
  trialsLeft,
  trialLimit,
  onClick,
}: SchoolPlanTrialsCounterProps) {
  // Fill reflects what's left, not what's used — mirrors the teaching-sessions
  // widget above where a full bar = all sessions available and an empty bar =
  // all sessions used up.
  const remainingWidth = trialLimit > 0 ? (trialsLeft / trialLimit) * 100 : 0;

  return (
    <Container onClick={onClick} role={onClick ? 'button' : undefined}>
      <LabelRow>
        <Label>
          {trialsLeft === 1 ? (
            <T
              keyName="SchoolPlanTrialsCounter.Label.Singular"
              defaultValue="1 school plan trial left"
            />
          ) : (
            <T
              keyName="SchoolPlanTrialsCounter.Label.Plural"
              defaultValue="{count} school plan trials left"
              params={{ count: trialsLeft }}
            />
          )}
        </Label>
        <HoverChevron>
          <ChevronRight color={theme.colors.greyDark} />
        </HoverChevron>
      </LabelRow>
      <BarTrack>
        <BarFill style={{ width: `${remainingWidth}%` }} />
      </BarTrack>
      <Sublabel>
        <T
          keyName="SchoolPlanTrialsCounter.Sublabel"
          defaultValue="One-time trial · doesn't renew"
        />
      </Sublabel>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.greyDark};
  line-height: 1;
`;

const HoverChevron = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Container}:hover & {
    opacity: 0.6;
  }
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #e8e0d8;
  border-radius: 3px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${theme.colors.orange};
  transition: width 0.3s ease;
`;

const Sublabel = styled.span`
  font-size: 11px;
  color: ${theme.colors.greyDark};
  opacity: 0.6;
  line-height: 1;
`;
